import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './Features.module.css';

const FeatureScreenshot = ({ src, alt, label, hint }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const showImage = src && loaded && !failed;

  const openLightbox = useCallback(() => {
    if (showImage) {
      setLightboxOpen(true);
    }
  }, [showImage]);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  useEffect(() => {
    if (!lightboxOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeLightbox();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxOpen, closeLightbox]);

  return (
    <>
      <figure className={styles.screenshot}>
        {src && !failed && (
          <div className={styles.screenshotMedia}>
            <img
              src={src}
              alt={alt}
              className={styles.screenshotImage}
              onLoad={() => setLoaded(true)}
              onError={() => setFailed(true)}
              style={{ display: showImage ? 'block' : 'none' }}
            />
            {showImage && (
              <button
                type="button"
                className={styles.screenshotZoomBtn}
                onClick={openLightbox}
                aria-label={`Agrandir : ${alt || label}`}
                title="Agrandir l'aperçu"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-4-4" />
                  <path d="M11 8v6M8 11h6" />
                </svg>
              </button>
            )}
          </div>
        )}
        {!showImage && (
          <div className={styles.screenshotFrame}>
            <div>
              <p className={styles.screenshotLabel}>📷 {label}</p>
              {hint && <p className={styles.screenshotHint}>{hint}</p>}
            </div>
          </div>
        )}
        {alt && <figcaption className={styles.screenshotCaption}>{alt}</figcaption>}
      </figure>

      {lightboxOpen && showImage && createPortal(
        <div
          className={styles.screenshotLightboxBackdrop}
          onClick={closeLightbox}
          role="presentation"
        >
          <div
            className={styles.screenshotLightboxDialog}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={alt || label}
          >
            <button
              type="button"
              className={styles.screenshotLightboxClose}
              onClick={closeLightbox}
              aria-label="Fermer l'aperçu agrandi"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
            <img src={src} alt={alt} className={styles.screenshotLightboxImage} />
            {alt && <p className={styles.screenshotLightboxCaption}>{alt}</p>}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default FeatureScreenshot;
